import { SessionItem } from "@/mds/components/sessions/session-item";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import {
  ArtifactV1SessionPartKind,
  SessionArtifactPayload,
  MDSSessionConfig,
  SessionEvent,
  SessionStatus,
} from "@/mds/components/sessions/session.types";

export type MDSSessionArtifact = {
  kind: ArtifactV1SessionPartKind;
  payload: SessionArtifactPayload;
};

export interface ReasoningGroupState {
  isCollapsed: boolean;
  userToggled: boolean;
}

export interface MDSSessionState {
  activeArtifact: MDSSessionArtifact | null;
  sessionEvents: SessionEvent[];
  sessionStatus: SessionStatus;
  isMinimized: boolean;
  reasoningGroups: Record<string, ReasoningGroupState>;
  countdownDurationMs: number;
  isCountdownActive: boolean;
  isCountdownPaused: boolean;
  /**
   * Indicates whether the session is currently in the special "notification"
   * view mode that is shown directly after transitioning from Processing → Finished.
   */
  isInNotificationState: boolean;
  isInputFocused: boolean;
  isHovering: boolean;
}

export enum MDSSessionAction {
  SetActiveArtifact = "SET_ACTIVE_ARTIFACT",
  SetExtensionFocusState = "SET_EXTENSION_FOCUS_STATE",
  SetExtensionHoverState = "SET_EXTENSION_HOVER_STATE",
  SetExtensionInputFocusState = "SET_EXTENSION_INPUT_FOCUS_STATE",
  AddSessionEvent = "ADD_SESSION_EVENT",
  UpdateSessionEvents = "UPDATE_SESSION_EVENTS",
  UpdateSessionStatus = "UPDATE_SESSION_STATUS",
  MinimizeSession = "MINIMIZE_SESSION",
  ExpandSession = "EXPAND_SESSION",
  ToggleReasoningGroup = "TOGGLE_REASONING_GROUP",
  AutoCollapseReasoningGroup = "AUTO_COLLAPSE_REASONING_GROUP",
  StartCountdown = "START_COUNTDOWN",
  StopCountdown = "STOP_COUNTDOWN",
  PauseCountdown = "PAUSE_COUNTDOWN",
  ResumeCountdown = "RESUME_COUNTDOWN",
  SetInputFocused = "SET_INPUT_FOCUSED",
  SetHovering = "SET_HOVERING",
  /**
   * Sets whether the session should be displayed in notification state.
   */
  SetNotificationState = "SET_NOTIFICATION_STATE",
}

export interface MDSSessionViewArtifactAction {
  type: MDSSessionAction.SetActiveArtifact;
  payload: MDSSessionArtifact | null;
}

export interface MDSSessionSetExtensionFocusStateAction {
  type: MDSSessionAction.SetExtensionFocusState;
  payload: boolean;
}

export interface MDSSessionSetExtensionHoverStateAction {
  type: MDSSessionAction.SetExtensionHoverState;
  payload: boolean;
}

export interface MDSSessionSetExtensionInputFocusStateAction {
  type: MDSSessionAction.SetExtensionInputFocusState;
  payload: boolean;
}

export interface MDSSessionAddEventAction {
  type: MDSSessionAction.AddSessionEvent;
  payload: SessionEvent;
}

export interface MDSSessionUpdateEventsAction {
  type: MDSSessionAction.UpdateSessionEvents;
  payload: SessionEvent[];
}

export interface MDSSessionUpdateStatusAction {
  type: MDSSessionAction.UpdateSessionStatus;
  payload: SessionStatus;
}

export interface MDSSessionMinimizeAction {
  type: MDSSessionAction.MinimizeSession;
}

export interface MDSSessionExpandAction {
  type: MDSSessionAction.ExpandSession;
}

export interface MDSSessionToggleReasoningGroupAction {
  type: MDSSessionAction.ToggleReasoningGroup;
  payload: { groupId: string };
}

export interface MDSSessionAutoCollapseReasoningGroupAction {
  type: MDSSessionAction.AutoCollapseReasoningGroup;
  payload: { groupId: string };
}

export interface MDSSessionStartCountdownAction {
  type: MDSSessionAction.StartCountdown;
  payload: { durationMs: number };
}

export interface MDSSessionStopCountdownAction {
  type: MDSSessionAction.StopCountdown;
}

export interface MDSSessionPauseCountdownAction {
  type: MDSSessionAction.PauseCountdown;
}

export interface MDSSessionResumeCountdownAction {
  type: MDSSessionAction.ResumeCountdown;
}

export interface MDSSessionSetInputFocusedAction {
  type: MDSSessionAction.SetInputFocused;
  payload: boolean;
}

export interface MDSSessionSetHoveringAction {
  type: MDSSessionAction.SetHovering;
  payload: boolean;
}

export interface MDSSessionSetNotificationStateAction {
  type: MDSSessionAction.SetNotificationState;
  payload: boolean;
}

export type MDSSessionActionTypes =
  | MDSSessionViewArtifactAction
  | MDSSessionSetExtensionFocusStateAction
  | MDSSessionSetExtensionHoverStateAction
  | MDSSessionSetExtensionInputFocusStateAction
  | MDSSessionAddEventAction
  | MDSSessionUpdateEventsAction
  | MDSSessionUpdateStatusAction
  | MDSSessionMinimizeAction
  | MDSSessionExpandAction
  | MDSSessionToggleReasoningGroupAction
  | MDSSessionAutoCollapseReasoningGroupAction
  | MDSSessionStartCountdownAction
  | MDSSessionStopCountdownAction
  | MDSSessionPauseCountdownAction
  | MDSSessionResumeCountdownAction
  | MDSSessionSetInputFocusedAction
  | MDSSessionSetHoveringAction
  | MDSSessionSetNotificationStateAction;

export interface MDSSessionContextValue {
  // State
  activeArtifact: MDSSessionArtifact | null;
  sessionEvents: SessionEvent[];
  sessionItems: SessionItem[];
  sessionStatus: SessionStatus;
  sessionViewMode: SessionViewMode;
  sessionConfig: MDSSessionConfig;
  reasoningGroups: Record<string, ReasoningGroupState>;
  countdownDurationMs: number;
  isCountdownPaused: boolean;
  isSessionTitleVisible: boolean;
  isCountdownVisible: boolean;
  isInputFormVisible: boolean;
  isSessionViewExpanded: boolean;

  // Actions
  viewArtifact: ({ payload }: { payload: MDSSessionArtifact }) => void;
  closeArtifact: () => void;
  addSessionEvent: (event: SessionEvent) => void;
  updateSessionEvents: (events: SessionEvent[]) => void;
  updateSessionStatus: (status: SessionStatus) => void;
  minimizeSession: () => void;
  expandSession: () => void;
  expandFromNotification: () => void;
  toggleReasoningGroup: (groupId: string) => void;
  autoCollapseReasoningGroup: (groupId: string) => void;
  startCountdown: (durationMs: number) => void;
  stopCountdown: () => void;
  onCountdownComplete: () => void;
  handleHoverExtension: () => void;
  handleUnhoverExtension: () => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleInputChange: () => void;
}
